export type ScaleMode = "visual" | "true";

export interface ScaleParams {
  /** Exponent applied to (km * distanceGain); 1 is linear, 0.5 is square-root compression. */
  distanceExponent: number;
  distanceGain: number;
  radiusGain: number;
  /** Minimum on-screen radius for textured and simple bodies, in pixels. */
  minPixels: number;
}

export const TRUE_SCALE: ScaleParams = {
  distanceExponent: 1,
  distanceGain: 1e-6,
  radiusGain: 1e-6,
  minPixels: 0,
};
export const VISUAL_SCALE: ScaleParams = {
  distanceExponent: 0.5,
  distanceGain: 2.2e-4,
  radiusGain: 5.7e-5,
  minPixels: 3,
};

export function paramsFor(mode: ScaleMode): ScaleParams {
  return mode === "true" ? TRUE_SCALE : VISUAL_SCALE;
}

const mix = (a: number, b: number, t: number): number =>
  t <= 0 ? a : t >= 1 ? b : a + (b - a) * t;
const mixLog = (a: number, b: number, t: number): number =>
  t <= 0 ? a : t >= 1 ? b : Math.exp(mix(Math.log(a), Math.log(b), t));

export class ScaleMapper {
  readonly params: ScaleParams = { ...VISUAL_SCALE };
  version = 0;

  set(p: ScaleParams): void {
    Object.assign(this.params, p);
    this.version++;
  }

  lerp(from: ScaleParams, to: ScaleParams, t: number): void {
    this.params.distanceExponent = mix(from.distanceExponent, to.distanceExponent, t);
    this.params.distanceGain = mixLog(from.distanceGain, to.distanceGain, t);
    this.params.radiusGain = mixLog(from.radiusGain, to.radiusGain, t);
    this.params.minPixels = mix(from.minPixels, to.minPixels, t);
    this.version++;
  }

  mapDistance(km: number): number {
    return Math.pow(km * this.params.distanceGain, this.params.distanceExponent);
  }

  mapRadius(km: number): number {
    return km * this.params.radiusGain;
  }
}
